import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ArrowRight,
  Search,
  MapPin,
  Building2,
  Users,
  Briefcase,
  Rocket,
  MessageSquare,
  Database,
  TrendingUp,
  Shield,
  Sparkles,
  CreditCard,
  Lock,
  ThumbsUp,
  ThumbsDown,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

// ---------------------------------------------------------------------------
// Extracted components
// ---------------------------------------------------------------------------

function HeroIllustration() {
  const { t } = useTranslation("marketing");
  return (
    <div className="relative mx-auto w-full max-w-md md:mx-0">
      <div className="rounded-xl border bg-card p-4 shadow-card">
        {/* Search bar */}
        <div className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {t("landing.illustrationSearch")}
          </span>
        </div>

        {/* Progress indicator */}
        <div className="my-3 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-primary/10">
            <div className="h-full w-1/2 animate-pulse rounded-full bg-primary/40" />
          </div>
          <span className="text-xs font-medium text-primary">
            {t("landing.illustrationHits")}
          </span>
        </div>

        {/* Result cards */}
        <div className="flex flex-col gap-2.5">
          <div className="rounded-lg border bg-background p-3 transition-shadow hover:shadow-md">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-semibold">TechVision GmbH</span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                95% Match
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Software &amp; IT
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                M&uuml;nchen
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                32 Mitarbeiter
              </span>
            </div>
          </div>

          <div className="rounded-lg border bg-background p-3 transition-shadow hover:shadow-md">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-semibold">
                DataFlow Solutions AG
              </span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                88% Match
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                SaaS
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                M&uuml;nchen
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                18 Mitarbeiter
              </span>
            </div>
          </div>

          {/* Third card faded -- implies more results */}
          <div className="rounded-lg border bg-background p-3 opacity-60">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-semibold">CloudBridge IT GmbH</span>
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                82% Match
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Cloud Services
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                Garching b. M&uuml;nchen
              </span>
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                45 Mitarbeiter
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative blur elements */}
      <div className="absolute -right-4 -top-4 -z-10 h-24 w-24 rounded-full bg-accent/20 blur-2xl" />
      <div className="absolute -bottom-6 -left-6 -z-10 h-32 w-32 rounded-full bg-primary/10 blur-2xl" />
    </div>
  );
}

function PersonaCard({
  icon: Icon,
  title,
  pain,
  solution,
  helpsLabel,
}: {
  icon: LucideIcon;
  title: string;
  pain: string;
  solution: string;
  helpsLabel: string;
}) {
  return (
    <Card className="border bg-card shadow-card transition-shadow hover:shadow-md">
      <CardContent className="p-6">
        <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <h3 className="mb-2 text-lg font-semibold">{title}</h3>
        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          {pain}
        </p>
        <p className="text-sm leading-relaxed">
          {/* "FirmenFinden" brand name is hardcoded; only the verb phrase is translated */}
          <span className="font-bold text-primary">
            FirmenFinden {helpsLabel}
          </span>{" "}
          {solution}
        </p>
      </CardContent>
    </Card>
  );
}

function WalkthroughStep({
  step,
  title,
  caption,
  children,
}: {
  step: number;
  title: string;
  caption: string;
  children: ReactNode;
}) {
  return (
    <div className="relative md:pl-24">
      <div className="absolute left-0 top-0 z-10 hidden h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground shadow-card md:flex">
        {step}
      </div>
      <Card className="overflow-hidden shadow-card">
        <CardContent className="p-6 sm:p-8">
          <div className="mb-4 flex items-center gap-3 md:hidden">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-primary-foreground">
              {step}
            </div>
            <h3 className="text-xl font-bold">{title}</h3>
          </div>
          <h3 className="mb-4 hidden text-xl font-bold md:block">{title}</h3>

          {children}

          <p className="text-sm text-muted-foreground">{caption}</p>
        </CardContent>
      </Card>
    </div>
  );
}

function TrustBadge({
  icon: Icon,
  label,
}: {
  icon: LucideIcon;
  label: string;
}) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-background px-4 py-2 text-xs font-medium text-muted-foreground shadow-sm sm:text-sm">
      <Icon className="h-4 w-4 text-primary" />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Step illustration content (keeps the main render lean)
// ---------------------------------------------------------------------------

function Step1Illustration() {
  const { t } = useTranslation("marketing");
  return (
    <>
      {/* Illustrated textarea */}
      <div className="mb-4 rounded-lg border-2 border-primary/20 bg-background p-4">
        <div className="mb-2 flex items-center gap-2">
          <Search className="h-4 w-4 text-muted-foreground" />
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("landing.step1QueryLabel")}
          </span>
        </div>
        <p className="text-sm italic leading-relaxed sm:text-base">
          {t("landing.step1QueryText")}
        </p>
        <div className="mt-1 h-px w-full bg-primary/10" />
        <div className="mt-1 h-px w-3/4 bg-primary/10" />
      </div>

      {/* Auto-generated tags */}
      <div className="mb-4 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <Building2 className="h-3 w-3" />
          Software
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          <MapPin className="h-3 w-3" />
          Bayern
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
          Cloud-Dienste
        </span>
      </div>
    </>
  );
}

function Step2Illustration() {
  return (
    <div className="relative mb-4 h-48 sm:h-44">
      {/* Card 3 (back) */}
      <div className="absolute left-4 right-0 top-0 rounded-lg border border-primary/10 bg-card p-3 opacity-60 shadow-sm">
        <div className="mb-2 h-3 w-32 rounded bg-primary/15" />
        <div className="flex gap-2">
          <span className="h-2.5 w-16 rounded bg-primary/10" />
          <span className="h-2.5 w-20 rounded bg-primary/10" />
        </div>
      </div>
      {/* Card 2 (middle) */}
      <div className="absolute left-2 right-2 top-10 rounded-lg border border-primary/15 bg-card p-3 opacity-80 shadow-sm">
        <div className="mb-2 flex items-center gap-2">
          <div className="h-3.5 w-36 rounded bg-primary/20" />
        </div>
        <div className="flex gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            <MapPin className="h-2.5 w-2.5" />
            Stuttgart
          </span>
          <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            IT-Dienste
          </span>
        </div>
      </div>
      {/* Card 1 (front) */}
      <div className="absolute left-0 right-4 top-20 rounded-lg border border-primary/20 bg-card p-4 shadow-card">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-semibold">CloudTech GmbH</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
            Relevanz: 94%
          </span>
        </div>
        <div className="mb-2 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            <MapPin className="h-2.5 w-2.5" />
            M&uuml;nchen
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            <Building2 className="h-2.5 w-2.5" />
            Cloud-Software
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
            <Users className="h-2.5 w-2.5" />
            32 Mitarbeiter
          </span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div className="h-full w-3/4 animate-pulse rounded-full bg-primary/40" />
        </div>
      </div>
    </div>
  );
}

function Step3Illustration() {
  const { t } = useTranslation("marketing");
  return (
    <div className="mb-4 max-w-sm rounded-lg border border-primary/20 bg-card p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold">DataVision AG</span>
        <span className="inline-flex items-center rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-medium text-accent">
          Relevanz: 87%
        </span>
      </div>
      <div className="mb-3 flex gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
          <MapPin className="h-2.5 w-2.5" />
          N&uuml;rnberg
        </span>
        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary">
          Datenanalyse
        </span>
      </div>
      <div className="flex items-center gap-3 border-t pt-3">
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-success/10 px-3 py-1.5 text-xs font-medium text-success ring-1 ring-success/20">
          <ThumbsUp className="h-3.5 w-3.5" />
          {t("landing.relevant")}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-lg bg-destructive/10 px-3 py-1.5 text-xs font-medium text-destructive ring-1 ring-destructive/20">
          <ThumbsDown className="h-3.5 w-3.5" />
          {t("landing.notRelevant")}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

const LandingPage = () => {
  const { isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation("marketing");

  // Authenticated users skip the landing page
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/search/new" replace />;
  }

  const personas = [
    {
      icon: Users,
      title: t("landing.persona_sales_title"),
      pain: t("landing.persona_sales_pain"),
      solution: t("landing.persona_sales_solution"),
    },
    {
      icon: Briefcase,
      title: t("landing.persona_agencies_title"),
      pain: t("landing.persona_agencies_pain"),
      solution: t("landing.persona_agencies_solution"),
    },
    {
      icon: Rocket,
      title: t("landing.persona_founders_title"),
      pain: t("landing.persona_founders_pain"),
      solution: t("landing.persona_founders_solution"),
    },
  ] as const;

  const trustBadges = [
    { icon: Shield, label: t("landing.trust_gdpr") },
    { icon: Sparkles, label: t("landing.trust_ai") },
    { icon: CreditCard, label: t("landing.trust_free") },
    { icon: Lock, label: t("landing.trust_eu") },
  ] as const;

  const differentiators = [
    {
      icon: MessageSquare,
      title: t("landing.diff1Title"),
      description: t("landing.diff1Description"),
    },
    {
      icon: Database,
      title: t("landing.diff2Title"),
      description: t("landing.diff2Description"),
    },
    {
      icon: TrendingUp,
      title: t("landing.diff3Title"),
      description: t("landing.diff3Description"),
    },
  ] as const;

  // "FirmenFinden hilft:" — split so brand stays hardcoded; we extract only the verb phrase
  const helpsLabel = t("landing.personaHelps").replace("FirmenFinden ", "");

  return (
    <div>
      {/* Hero */}
      <section className="container py-20 md:py-32">
        <div className="grid items-center gap-12 md:grid-cols-2 md:gap-16">
          {/* Left: Copy */}
          <div className="max-w-xl">
            <span className="mb-4 inline-block rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              {t("landing.heroBadge")}
            </span>
            <h1 className="mb-5 text-4xl font-bold leading-tight tracking-tight md:text-5xl">
              {t("landing.heroTitle")}{" "}
              <span className="text-primary">
                {t("landing.heroTitleHighlight")}
              </span>
            </h1>
            <p className="mb-8 text-lg leading-relaxed text-muted-foreground">
              {t("landing.heroSubtitle")}
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/signup">
                  {t("landing.ctaStart")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="#so-funktionierts">{t("landing.ctaHowItWorks")}</a>
              </Button>
            </div>
          </div>

          {/* Right: CSS-only product illustration */}
          <HeroIllustration />
        </div>
      </section>

      {/* Target audience */}
      <section className="border-t bg-muted/30">
        <div className="container py-16 md:py-24">
          <h2 className="mb-4 text-center text-2xl font-bold tracking-tight md:text-3xl">
            {t("landing.audienceTitle")}
          </h2>
          <p className="mx-auto mb-12 max-w-2xl text-center text-muted-foreground">
            {t("landing.audienceSubtitle")}
          </p>

          <div className="grid gap-6 md:grid-cols-3">
            {personas.map((p) => (
              <PersonaCard key={p.title} {...p} helpsLabel={helpsLabel} />
            ))}
          </div>
        </div>
      </section>

      {/* Product walkthrough */}
      <section id="so-funktionierts" className="container py-20">
        <div className="mx-auto max-w-4xl">
          <h2 className="mb-16 text-center text-2xl font-bold tracking-tight md:text-3xl">
            {t("landing.howItWorksTitle")}
          </h2>

          <div className="relative">
            {/* Vertical connector line -- desktop only */}
            <div className="absolute bottom-16 left-8 top-16 hidden w-px border-l-2 border-dashed border-primary/30 md:block" />

            <div className="space-y-12 md:space-y-16">
              <WalkthroughStep
                step={1}
                title={t("landing.step1Title")}
                caption={t("landing.step1Caption")}
              >
                <Step1Illustration />
              </WalkthroughStep>

              <WalkthroughStep
                step={2}
                title={t("landing.step2Title")}
                caption={t("landing.step2Caption")}
              >
                <Step2Illustration />
              </WalkthroughStep>

              <WalkthroughStep
                step={3}
                title={t("landing.step3Title")}
                caption={t("landing.step3Caption")}
              >
                <Step3Illustration />
              </WalkthroughStep>
            </div>
          </div>
        </div>
      </section>

      {/* Key differentiators */}
      <section className="border-t bg-muted/30">
        <div className="container py-16 md:py-24">
          <h2 className="mb-14 text-center text-2xl font-bold tracking-tight md:text-3xl">
            {t("landing.whyTitle")}
          </h2>

          <div className="mx-auto grid max-w-5xl gap-10 md:grid-cols-3 md:gap-12">
            {differentiators.map((d) => (
              <div key={d.title} className="text-center md:text-left">
                <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
                  <d.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{d.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {d.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust strip + Bottom CTA */}
      <section className="bg-primary/5 py-20">
        {/* Trust strip */}
        <div className="container mb-14">
          <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
            {trustBadges.map((b) => (
              <TrustBadge key={b.label} {...b} />
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="container text-center">
          <div className="mx-auto max-w-2xl">
            <h2 className="mb-4 text-2xl font-bold tracking-tight sm:text-3xl">
              {t("landing.ctaBottomTitle")}
            </h2>
            <p className="mb-8 text-base leading-relaxed text-muted-foreground sm:text-lg">
              {t("landing.ctaBottomSubtitle")}
            </p>
            <Button asChild size="lg" className="shadow-sm">
              <Link to="/signup">
                {t("landing.ctaBottomButton")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
